import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Flex, List, Skeleton, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PrintButton } from '../../components/print/PrintButton';
import { RefundDrawer, RefundList } from '../../components/RefundDrawer';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import { formatMoney, isPositive } from '../../lib/money';
import { getBookingMoney } from './bookings-api';

const { Text } = Typography;

/**
 * What the cashier has taken on this booking, the credit it still holds, and
 * the invoice once the sale is billed.
 *
 * The invoice is plain text, never a link: an advisor holds no
 * pos.invoice_view, and a link they cannot open is worse than none.
 */
export function BookingMoneyCard({ bookingUid }: { bookingUid: string }): ReactNode {
  const { t } = useTranslation();
  const [refunding, setRefunding] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['booking-money', bookingUid],
    queryFn: () => getBookingMoney(bookingUid),
  });

  return (
    <Card title={t('bookings.money.title')}>
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : data === undefined ? (
        <Alert
          type="error"
          showIcon
          message={error instanceof ApiError ? error.message : t('bookings.money.loadFailed')}
        />
      ) : (
        <Flex vertical gap={12}>
          {data.receipts.length === 0 ? (
            <Text type="secondary">{t('bookings.money.empty')}</Text>
          ) : (
            <List
              size="small"
              dataSource={data.receipts}
              rowKey={(r) => r.uid}
              renderItem={(r) => (
                <List.Item>
                  <Flex justify="space-between" align="flex-start" gap={12} style={{ width: '100%' }}>
                    <Flex vertical gap={2}>
                      <Flex gap={8} align="center">
                        <Text strong className="tabular">
                          {r.receiptNo}
                        </Text>
                        {r.isRefund && <Tag>{t('bookings.money.refundTag')}</Tag>}
                      </Flex>
                      <Text type="secondary">
                        {formatDateTime(r.receivedAt)} · {t(`till.method.${r.method}`)}
                      </Text>
                    </Flex>
                    <Flex align="center" gap={8}>
                      <Text strong>{formatMoney(r.amount)}</Text>
                      {r.printable && (
                        <PrintButton doc="receipt" uid={r.uid} label={t('print.printReceipt')} size="small" iconOnly />
                      )}
                    </Flex>
                  </Flex>
                </List.Item>
              )}
            />
          )}
          <Flex vertical gap={2}>
            <Text strong>{t('bookings.money.credit', { amount: formatMoney(data.credit.held) })}</Text>
            {isPositive(data.credit.forfeited) && (
              <Text>{t('bookings.money.forfeited', { amount: formatMoney(data.credit.forfeited) })}</Text>
            )}
            {isPositive(data.credit.refunded) && (
              <Text>{t('bookings.money.refunded', { amount: formatMoney(data.credit.refunded) })}</Text>
            )}
            {isPositive(data.credit.notRefundable) && (
              <Text>{t('bookings.money.notRefundable', { amount: formatMoney(data.credit.notRefundable) })}</Text>
            )}
          </Flex>
          <RefundList refunds={data.refunds} printPaid />
          {data.canRefund && (
            <>
              <Button
                danger
                onClick={() => {
                  setRefunding(true);
                }}
              >
                {t('refunds.refund')}
              </Button>
              <RefundDrawer
                source={{ bookingUid }}
                refundable={data.credit.refundable}
                open={refunding}
                onClose={() => {
                  setRefunding(false);
                }}
                onDone={() => undefined}
              />
            </>
          )}
          {data.invoice !== null && (
            <Text>
              {t('bookings.money.invoice', {
                invoiceNo: data.invoice.invoiceNo,
                status: t(`till.status.${data.invoice.status}`),
                total: formatMoney(data.invoice.grandTotal),
                balance: formatMoney(data.invoice.balanceDue),
              })}
            </Text>
          )}
        </Flex>
      )}
    </Card>
  );
}
